/**
 * Maps bidirectional routes between Legacy and Phoenix using manually synced templates, matching route intents across both apps without a shared package.
 */
export const PLATFORM_SWITCHER_ROUTE_KEYS = [
  'dashboard',
  'createSurvey',
  'editSurvey',
  'manageSurvey',
  'launchSurvey',
] as const;

export type PlatformSwitcherRouteKey =
  (typeof PLATFORM_SWITCHER_ROUTE_KEYS)[number];

interface PlatformSwitcherRouteTemplate {
  legacy: string;
  phoenix: string;
}

export const PLATFORM_SWITCHER_ROUTE_MAP: Record<
  PlatformSwitcherRouteKey,
  PlatformSwitcherRouteTemplate
> = {
  // dashboard.module.ts -> { path: 'dashboard', children: [{ path: '' }] }
  dashboard: { legacy: 'dashboard', phoenix: 'dashboard' },
  // create-survey.module.ts -> { path: 'create-surveys' }
  createSurvey: { legacy: 'create-surveys', phoenix: 'surveys/new' },
  // create-survey.module.ts -> { path: 'surveys', children: [{ path: ':id/edit' }] }
  editSurvey: { legacy: 'surveys/:id/edit', phoenix: 'surveys/:id/edit' },
  // dashboard.module.ts -> { path: 'dashboard', children: [{ path: ':id' }] }
  manageSurvey: { legacy: 'dashboard/:id', phoenix: 'surveys/:id' },
  // buyer.module.ts -> { path: 'launch-survey/:id' }
  launchSurvey: { legacy: 'launch-survey/:id', phoenix: 'surveys/:id/launch' },
};

function replaceParam(
  path: string,
  param: string,
  value: string | number
): string {
  return path.replace(`:${param}`, String(value));
}

function interpolate(
  template: string,
  params: Record<string, string | number>
): string {
  return Object.entries(params).reduce(
    (path, [param, value]) => replaceParam(path, param, value),
    template
  );
}

export function toPhoenixPath(
  routeKey: PlatformSwitcherRouteKey,
  params: Record<string, string | number> = {}
): string {
  return `/${interpolate(
    PLATFORM_SWITCHER_ROUTE_MAP[routeKey].phoenix,
    params
  )}`;
}

function toRegex(template: string): RegExp {
  const pattern = template.replace(/:([^/]+)/g, '(?<$1>[^/]+)');
  return new RegExp(`^/${pattern}/?$`);
}

export interface PlatformSwitcherRouteMatch {
  routeKey: PlatformSwitcherRouteKey;
  params: Record<string, string>;
}

/**
 * Maps a Legacy pathname to its switcher intent, relying on strict route ordering so exact literal paths take priority over parameter patterns.
 */
export function matchLegacyPath(
  pathname: string
): PlatformSwitcherRouteMatch | null {
  for (const routeKey of PLATFORM_SWITCHER_ROUTE_KEYS) {
    const match = toRegex(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].legacy).exec(
      pathname
    );
    if (match) {
      return { routeKey, params: match.groups ?? {} };
    }
  }
  return null;
}
